import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Video,
  Send,
  ExternalLink,
  CheckCircle2,
  Loader2,
  Sparkles,
  Calendar as CalendarIcon,
  Clock,
  Building2,
  User,
  Users,
  Copy,
  AlertTriangle,
  Radio,
  Film,
  Trash2,
  Save as SaveIcon,
  RefreshCw,
  FileText as FileTextIcon,
  Mic,
  Cloud,
  CircleOff,
  Hourglass,
  XCircle,
  Wand2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { api, formatDateTimeBR, ROLES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export default function MeetingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [minutes, setMinutes] = useState("");
  const [pastedAta, setPastedAta] = useState("");
  const [savingAta, setSavingAta] = useState(false);
  const [savingMinutes, setSavingMinutes] = useState(false);
  const [opening, setOpening] = useState(false);
  // Vimeo
  const [vimeoUrl, setVimeoUrl] = useState("");
  const [savingVimeo, setSavingVimeo] = useState(false);
  // Melhoria 1 (2026-06-17): estado do unlink Drive (DEVE ficar antes do early return)
  const [unlinkingDrive, setUnlinkingDrive] = useState(false);
  // Fase 9 — Auto recovery
  const [recovering, setRecovering] = useState(false);
  const popupRef = useRef(null);
  const popupWatcherRef = useRef(null);

  const refresh = async () => {
    const { data } = await api.get(`/meetings/${id}`);
    setMeeting(data);
    setMinutes(data.minutes_text || "");
    setVimeoUrl(data.vimeo_url || "");
  };

  useEffect(() => {
    refresh();
    return () => {
      if (popupWatcherRef.current) clearInterval(popupWatcherRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!meeting) return <Skeleton className="h-96" />;

  const canEditMinutes =
    user.role === ROLES.CONSULTOR ||
    user.role === ROLES.SECRETARIA ||
    user.role === ROLES.DIRETORA;

  const hasMeet =
    meeting.room_provider === "google_meet" && meeting.room_url;

  // ---- Enter meeting in centered popup ----
  const enterMeet = async () => {
    if (!hasMeet) {
      toast.error("Esta reunião ainda não tem link do Google Meet.");
      return;
    }
    setOpening(true);

    // Mark as live (best-effort)
    api.post(`/meetings/${id}/mark-live`).catch(() => {});

    const w = Math.min(1100, window.screen.availWidth - 80);
    const h = Math.min(720, window.screen.availHeight - 80);
    const left = (window.screen.availWidth - w) / 2;
    const top = (window.screen.availHeight - h) / 2;

    const popup = window.open(
      meeting.room_url,
      "ceap-meet-room",
      `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!popup) {
      toast.info("Pop-up bloqueado. Abrindo em nova guia…");
      window.open(meeting.room_url, "_blank", "noopener,noreferrer");
      setOpening(false);
      return;
    }

    popup.focus?.();
    popupRef.current = popup;

    // Watch for popup close → refresh meeting (status may be DONE if ata pasted)
    if (popupWatcherRef.current) clearInterval(popupWatcherRef.current);
    popupWatcherRef.current = setInterval(() => {
      if (popup.closed) {
        clearInterval(popupWatcherRef.current);
        popupWatcherRef.current = null;
        setOpening(false);
        toast.success(
          "Reunião encerrada. Cole a ata gerada pelo Gemini logo abaixo."
        );
        refresh();
      }
    }, 1500);

    setOpening(false);
  };

  const copyMeetLink = async () => {
    if (!meeting.room_url) return;
    try {
      await navigator.clipboard.writeText(meeting.room_url);
      toast.success("Link copiado para a área de transferência.");
    } catch {
      toast.error("Não foi possível copiar o link.");
    }
  };

  // ---- Save / publish minutes ----
  const saveMinutes = async (publish = false) => {
    setSavingMinutes(true);
    try {
      await api.put(`/meetings/${id}/minutes`, {
        minutes_text: minutes,
        publish,
      });
      toast.success(
        publish ? "Ata publicada ao cliente." : "Ata salva como rascunho."
      );
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar ata.");
    } finally {
      setSavingMinutes(false);
    }
  };

  const savePastedAta = async () => {
    if (!pastedAta.trim()) {
      toast.error("Cole o texto da ata antes de salvar.");
      return;
    }
    setSavingAta(true);
    try {
      const { data } = await api.post(`/meetings/${id}/paste-minutes`, {
        text: pastedAta.trim(),
        source: "gemini_paste",
      });
      toast.success("Ata salva! Revise abaixo antes de publicar.");
      setMinutes(data.minutes_text || data.transcription_text);
      setPastedAta("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar.");
    } finally {
      setSavingAta(false);
    }
  };

  // ---- Vimeo recording link (Sec/Diretora) ----
  const saveVimeo = async () => {
    setSavingVimeo(true);
    try {
      await api.put(`/meetings/${id}/vimeo`, { url: vimeoUrl.trim() });
      toast.success(
        vimeoUrl.trim()
          ? "Link do Vimeo salvo. Vídeo já está disponível no player."
          : "Gravação removida.",
      );
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar.");
    } finally {
      setSavingVimeo(false);
    }
  };

  const removeVimeo = async () => {
    if (!window.confirm("Remover a gravação do Vimeo desta reunião?")) return;
    setVimeoUrl("");
    setSavingVimeo(true);
    try {
      await api.put(`/meetings/${id}/vimeo`, { url: "" });
      toast.success("Gravação removida.");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro.");
    } finally {
      setSavingVimeo(false);
    }
  };

  // ---- Melhoria 1 (2026-06-17): remover vínculo gravação Drive ----
  // (state `unlinkingDrive` já declarado no topo, antes do early return)
  const unlinkDriveRecording = async () => {
    const ok = window.confirm(
      "Tem certeza que deseja remover esta gravação da reunião?\n\n"
      + "• O arquivo original no Google Drive NÃO será excluído.\n"
      + "• Apenas o vínculo desta gravação com esta reunião será removido.\n"
      + "• A auto-vinculação ficará DESATIVADA para esta reunião — você "
      + "precisará cadastrar o link correto manualmente (via Vimeo)."
    );
    if (!ok) return;
    setUnlinkingDrive(true);
    try {
      const { data } = await api.delete(`/meetings/${id}/drive-recording`);
      if (data.changed) {
        toast.success("Vínculo da gravação removido. Cadastre o link manual (Vimeo) abaixo.");
      } else {
        toast.info(data.detail || "Nada a remover.");
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao remover vínculo.");
    } finally {
      setUnlinkingDrive(false);
    }
  };

  // ---- Phase 9: Auto Recovery (Google Meet + Drive) ----
  const triggerRecovery = async () => {
    setRecovering(true);
    try {
      const { data } = await api.post(`/meetings/${id}/recover`);
      setMeeting(data);
      setMinutes(data.minutes_text || "");
      setVimeoUrl(data.vimeo_url || "");
      const status = data.auto_recovery_status;
      if (status === "complete") {
        toast.success(
          "Recuperação completa! Gravação, transcrição e notas atualizadas."
        );
      } else if (status === "partial") {
        toast.info(
          "Recuperação parcial. O sistema continuará tentando automaticamente."
        );
      } else if (status === "waiting") {
        toast.info(
          "Google ainda está processando a reunião. Tente novamente em alguns minutos."
        );
      } else if (status === "failed") {
        toast.error(
          data.auto_recovery_error ||
            "Não foi possível recuperar. Verifique se as APIs Google estão autorizadas."
        );
      } else {
        toast.success("Tentativa de recuperação concluída.");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Erro ao tentar recuperar. Verifique sua conexão Google."
      );
    } finally {
      setRecovering(false);
    }
  };

  const isLive = meeting.status === "live";
  const isDone = meeting.status === "done";
  const inFuture = new Date(meeting.scheduled_at) > new Date();

  return (
    <div className="space-y-6" data-testid="meeting-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
        data-testid="meeting-back-button"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
      </button>

      {/* ===== HERO: Sala de Reunião ===== */}
      <Card
        className="card-soft overflow-hidden border-midnight/20"
        data-testid="meeting-room-card"
      >
        <div className="bg-gradient-to-br from-midnight via-midnight to-ink text-white px-6 py-7">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge className="bg-white/15 text-white border border-white/20 hover:bg-white/20">
                  <Video className="w-3 h-3 mr-1" /> Sala de Reunião CEAP
                </Badge>
                {isLive && (
                  <Badge
                    className="bg-red-500 text-white border-red-400 hover:bg-red-500 animate-pulse"
                    data-testid="badge-live"
                  >
                    <Radio className="w-3 h-3 mr-1" /> AO VIVO
                  </Badge>
                )}
                {isDone && (
                  <Badge
                    className="bg-emerald-500 text-white border-emerald-400 hover:bg-emerald-500"
                    data-testid="badge-done"
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Realizada
                  </Badge>
                )}
                <StatusBadge status={meeting.status} />
              </div>
              <h1
                className="text-2xl font-semibold leading-tight"
                data-testid="meeting-title"
              >
                {meeting.title}
              </h1>
              {meeting.agenda && (
                <p className="text-sm text-white/80 mt-2 max-w-2xl">
                  {meeting.agenda}
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/85">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarIcon className="w-4 h-4" />
                  {formatDateTimeBR(meeting.scheduled_at)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> {meeting.duration_minutes} min
                </span>
                {meeting.client_name && (
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="w-4 h-4" /> {meeting.client_name}
                  </span>
                )}
                {meeting.consultant_name && (
                  <span className="inline-flex items-center gap-1.5">
                    <User className="w-4 h-4" /> Consultor:{" "}
                    <strong className="font-semibold">
                      {meeting.consultant_name}
                    </strong>
                  </span>
                )}
                {meeting.participants?.length > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="w-4 h-4" />{" "}
                    {meeting.participants.length} participante
                    {meeting.participants.length === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* CTA action area */}
        <CardContent className="p-6">
          {hasMeet ? (
            <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center">
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Pronto para iniciar a reunião
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Clique abaixo para entrar no <strong>Google Meet</strong>{" "}
                  desta reunião. A sala abre em uma janela do CEAP — você pode
                  voltar aqui depois para colar a ata gerada pelo Gemini.
                </p>
                <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1 truncate max-w-md">
                    Link da sala:{" "}
                    <a
                      href={meeting.room_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-midnight underline truncate"
                    >
                      {meeting.room_url}
                    </a>
                  </span>
                  <button
                    type="button"
                    onClick={copyMeetLink}
                    className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700"
                    data-testid="meeting-copy-link"
                    title="Copiar link"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:items-stretch">
                <Button
                  onClick={enterMeet}
                  disabled={opening}
                  size="lg"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md min-w-[220px]"
                  data-testid="enter-meet-button"
                >
                  {opening ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Video className="w-5 h-5 mr-2" />
                  )}
                  Entrar na Sala (Meet)
                </Button>
                {meeting.google_calendar_html_link && (
                  <a
                    href={meeting.google_calendar_html_link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button
                      variant="outline"
                      className="w-full"
                      data-testid="meeting-open-calendar"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      Abrir no Google Calendar
                    </Button>
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <strong>Sala indisponível.</strong> Esta reunião ainda não
                possui link do Google Meet. Peça à Diretora para acessar{" "}
                <em>Configurações → Conectar Google Calendar</em>; depois
                recrie a reunião para que o link do Meet seja gerado
                automaticamente.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== Fase 9 — Status de Recuperação Automática ===== */}
      {canEditMinutes && hasMeet && (
        <RecoveryStatusCard
          meeting={meeting}
          onRecover={triggerRecovery}
          recovering={recovering}
        />
      )}

      {/* ===== Instruções Gemini ===== */}
      {hasMeet && (
        <Card
          className="card-soft border-blue-100 bg-blue-50/30"
          data-testid="gemini-instructions-card"
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-slate-900">
              <Sparkles className="w-4 h-4 text-blue-700" />
              Dica: Gere a ata automaticamente com o Gemini
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="text-sm text-slate-700 ml-4 list-decimal space-y-1.5">
              <li>
                Já no Google Meet, clique no botão{" "}
                <strong>“Take notes for me”</strong> (Gemini) no menu inferior.
              </li>
              <li>
                Conduza a reunião normalmente; o Gemini escuta e gera as notas
                no Google Drive ao final.
              </li>
              <li>
                Após a reunião, abra o documento{" "}
                <em>“Anotações da reunião”</em> no Drive e copie o conteúdo.
              </li>
              <li>
                Volte para esta página e cole o texto no campo{" "}
                <strong>“Colar ata do Gemini”</strong> abaixo.
              </li>
            </ol>
          </CardContent>
        </Card>
      )}

      {/* ===== Ata: paste from Gemini ===== */}
      {canEditMinutes && (
        <Card className="card-soft" data-testid="paste-ata-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-700" />
              Colar ata do Gemini
            </CardTitle>
          </CardHeader>
          <CardContent>
            {meeting.transcription_text ? (
              <div
                className="p-4 border border-emerald-200 bg-emerald-50/40 rounded-lg mb-4"
                data-testid="transcription-result"
              >
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="text-sm font-semibold text-emerald-900 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> Ata já registrada
                    {meeting.transcription_source === "gemini_paste" && (
                      <Badge className="ml-2 bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100 text-[10px] uppercase">
                        Gemini
                      </Badge>
                    )}
                  </div>
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed max-h-56 overflow-y-auto scroll-thin">
                  {meeting.transcription_text}
                </p>
                <p className="text-xs text-slate-500 mt-2 italic">
                  Você pode editar e publicar para o cliente no card abaixo.
                </p>
              </div>
            ) : null}

            <div className="border-2 border-dashed border-blue-300 bg-white rounded-lg p-4">
              <p className="text-sm text-slate-700 mb-3">
                Cole aqui o texto gerado pelo <strong>Gemini</strong> (arquivo
                “<em>Anotações da reunião</em>” no Drive). O conteúdo será
                copiado direto para a ata — você ainda pode revisar e editar
                antes de publicar para o cliente.
              </p>
              <textarea
                value={pastedAta}
                onChange={(e) => setPastedAta(e.target.value)}
                placeholder="Cole aqui o resumo / transcrição gerada pelo Gemini..."
                rows={8}
                className="w-full p-3 border border-slate-300 rounded-md text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="paste-ata-textarea"
              />
              <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                <p className="text-xs text-slate-500">
                  {pastedAta.length > 0 &&
                    `${pastedAta.length} caracteres · ${
                      pastedAta.split(/\s+/).filter(Boolean).length
                    } palavras`}
                </p>
                <Button
                  onClick={savePastedAta}
                  disabled={savingAta || !pastedAta.trim()}
                  className="bg-blue-700 hover:bg-blue-800 text-white"
                  data-testid="save-pasted-ata-button"
                >
                  {savingAta ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />{" "}
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> Salvar ata
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== Gravação da reunião (Melhoria 1 — Vimeo tem prioridade sobre Drive) ===== */}
      {(canEditMinutes ||
        meeting.vimeo_video_id ||
        meeting.drive_recording_embed_url ||
        meeting.recording_pending_manual ||
        meeting.manual_recording_removed_at) && (
        <Card className="card-soft" data-testid="vimeo-recording-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <Film className="w-4 h-4 text-emerald-700" />
              Gravação da reunião
              {/* Melhoria 1: prioridade Vimeo (manual) > Drive (auto) */}
              {meeting.vimeo_video_id && (
                <Badge
                  className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 ml-1"
                  data-testid="recording-source-vimeo-badge"
                >
                  Vimeo (manual)
                </Badge>
              )}
              {!meeting.vimeo_video_id && meeting.drive_recording_embed_url && (
                <Badge
                  className="bg-blue-100 text-blue-800 hover:bg-blue-100 ml-1"
                  data-testid="recording-source-drive-badge"
                >
                  Drive (auto)
                </Badge>
              )}
              {/* Confiança do match (Melhoria 3) */}
              {!meeting.vimeo_video_id &&
                meeting.drive_recording_embed_url &&
                typeof meeting.recording_match_confidence === "number" &&
                meeting.recording_match_confidence < 0.8 && (
                  <Badge
                    className="bg-amber-100 text-amber-800 hover:bg-amber-100 ml-1"
                    data-testid="recording-low-confidence-badge"
                    title={meeting.recording_match_reason || undefined}
                  >
                    Confiança {Math.round(meeting.recording_match_confidence * 100)}% — revisar
                  </Badge>
                )}
              {meeting.recording_pending_manual && !meeting.drive_recording_embed_url && (
                <Badge
                  className="bg-amber-100 text-amber-800 hover:bg-amber-100 ml-1"
                  data-testid="recording-pending-badge"
                >
                  Pendente de validação
                </Badge>
              )}
              {meeting.manual_recording_removed_at && (
                <Badge
                  className="bg-slate-100 text-slate-700 hover:bg-slate-100 ml-1"
                  data-testid="recording-manually-removed-badge"
                  title="Auto-vinculação desabilitada manualmente"
                >
                  Auto-vinculação desativada
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Player: prioriza Vimeo (manual, mais confiável), depois Drive */}
            {meeting.vimeo_video_id ? (
              <div
                className="relative w-full bg-slate-900 rounded-lg overflow-hidden"
                style={{ paddingBottom: "56.25%" }}
                data-testid="vimeo-player-wrapper"
              >
                <iframe
                  src={`https://player.vimeo.com/video/${meeting.vimeo_video_id}${
                    meeting.vimeo_hash ? `?h=${meeting.vimeo_hash}` : ""
                  }`}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={`Gravação: ${meeting.title}`}
                  data-testid="vimeo-player-iframe"
                />
              </div>
            ) : meeting.drive_recording_embed_url ? (
              <div
                className="relative w-full bg-slate-900 rounded-lg overflow-hidden"
                style={{ paddingBottom: "56.25%" }}
                data-testid="drive-player-wrapper"
              >
                <iframe
                  src={meeting.drive_recording_embed_url}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen"
                  allowFullScreen
                  title={`Gravação Drive: ${meeting.title}`}
                  data-testid="drive-player-iframe"
                />
              </div>
            ) : (
              <div className="p-6 border-2 border-dashed border-slate-200 rounded-lg text-center text-sm text-slate-500">
                <Film className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                {meeting.recording_pending_manual ? (
                  <>
                    <strong>Recuperação automática falhou por segurança.</strong>{" "}
                    Encontramos múltiplas gravações possíveis mas nenhuma com
                    confiança suficiente — para evitar erro de vinculação, o
                    sistema deixou este slot vazio. {canEditMinutes && "Cole o link do Vimeo abaixo."}
                  </>
                ) : (
                  <>
                    Nenhuma gravação disponível ainda.{" "}
                    {canEditMinutes
                      ? "A gravação será recuperada automaticamente após a reunião, ou cole um link do Vimeo abaixo."
                      : "A gravação aparecerá aqui assim que estiver disponível."}
                  </>
                )}
              </div>
            )}

            {/* Drive metadata + botão REMOVER (Melhoria 1) */}
            {meeting.drive_recording_embed_url && (
              <div
                className={`text-xs rounded-md p-3 border flex flex-wrap items-center gap-2 ${
                  meeting.vimeo_video_id
                    ? "bg-slate-50 border-slate-200 text-slate-600"
                    : "bg-blue-50/50 border-blue-100 text-slate-600"
                }`}
                data-testid="drive-recording-meta"
              >
                <Cloud className="w-3.5 h-3.5 text-blue-700 flex-shrink-0" />
                <span className="flex-1 min-w-0">
                  {meeting.vimeo_video_id
                    ? "Gravação Drive vinculada em segundo plano "
                    : "Gravação recuperada automaticamente do Google Drive"}
                  {meeting.recording_recovered_at && (
                    <> em {formatDateTimeBR(meeting.recording_recovered_at)}</>
                  )}
                  .{" "}
                  {meeting.recording_match_reason && (
                    <span className="italic block mt-1 text-slate-500">
                      Match: {meeting.recording_match_reason}
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  {meeting.drive_recording_view_url && (
                    <a
                      href={meeting.drive_recording_view_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-blue-700 underline hover:text-blue-900"
                      data-testid="drive-recording-open-link"
                    >
                      Abrir no Drive <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {canEditMinutes &&
                    (user.role === ROLES.SECRETARIA || user.role === ROLES.DIRETORA) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={unlinkDriveRecording}
                        disabled={unlinkingDrive}
                        className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                        title="Remover vínculo desta gravação (o arquivo no Drive não será excluído)"
                        data-testid="unlink-drive-recording-button"
                      >
                        {unlinkingDrive ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                        )}
                        Remover
                      </Button>
                    )}
                </div>
              </div>
            )}

            {/* Vimeo form (Sec/Dir only) — fallback manual */}
            {canEditMinutes &&
              (user.role === ROLES.SECRETARIA ||
                user.role === ROLES.DIRETORA) && (
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 space-y-2">
                  <Label className="text-xs text-slate-600">
                    {meeting.vimeo_video_id
                      ? "Link manual do Vimeo (prioritário — cliente vê este)"
                      : meeting.drive_recording_embed_url
                        ? "Link manual do Vimeo (substituirá a gravação Drive acima)"
                        : "Link do Vimeo (público ou com hash de privacidade)"}
                  </Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="url"
                      value={vimeoUrl}
                      onChange={(e) => setVimeoUrl(e.target.value)}
                      placeholder="https://vimeo.com/123456789 ou https://vimeo.com/123456789/abcdef"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid="vimeo-url-input"
                    />
                    <Button
                      onClick={saveVimeo}
                      disabled={savingVimeo}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white"
                      data-testid="vimeo-save-button"
                    >
                      {savingVimeo ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <SaveIcon className="w-4 h-4 mr-2" />
                      )}
                      Salvar
                    </Button>
                    {meeting.vimeo_video_id && (
                      <Button
                        onClick={removeVimeo}
                        disabled={savingVimeo}
                        variant="outline"
                        className="border-red-200 text-red-700 hover:bg-red-50"
                        data-testid="vimeo-remove-button"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 italic">
                    {meeting.manual_recording_removed_at
                      ? "A auto-vinculação Drive está DESATIVADA para esta reunião. Cadastre o link manual do Vimeo."
                      : meeting.vimeo_video_id
                        ? "Link Vimeo cadastrado — tem prioridade sobre qualquer gravação Drive."
                        : meeting.drive_recording_embed_url
                          ? "Salvar um link do Vimeo aqui substituirá o vídeo Drive acima (útil se a gravação foi vinculada erroneamente)."
                          : "Faça upload do arquivo no Vimeo e cole o link — o CEAP extrai o ID e incorpora o player."}
                  </p>
                </div>
              )}

            {meeting.vimeo_url && (
              <p className="text-xs text-slate-500 truncate">
                Link Vimeo:{" "}
                <a
                  href={meeting.vimeo_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-midnight underline"
                  data-testid="vimeo-source-link"
                >
                  {meeting.vimeo_url}
                </a>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== Ata: edit and publish ===== */}
      <Card className="card-soft" data-testid="minutes-card">
        <CardHeader>
          <CardTitle className="text-base">Ata da reunião</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {meeting.minutes_published && (
            <div className="p-3 border border-emerald-200 bg-emerald-50 rounded-md text-sm text-emerald-800">
              <CheckCircle2 className="inline w-4 h-4 mr-1" /> Ata publicada
              para o cliente.
            </div>
          )}
          <Textarea
            rows={12}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            placeholder={
              canEditMinutes
                ? "Conteúdo da ata… (ou cole do Gemini no card acima)"
                : "A ata ainda não foi publicada."
            }
            disabled={!canEditMinutes}
            data-testid="minutes-textarea"
          />
          {canEditMinutes && (
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => saveMinutes(false)}
                disabled={savingMinutes}
                data-testid="save-minutes-button"
              >
                {savingMinutes ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Salvar rascunho
              </Button>
              <Button
                onClick={() => saveMinutes(true)}
                disabled={savingMinutes || !minutes.trim()}
                className="bg-gold text-ink hover:bg-gold-deep hover:text-white"
                data-testid="publish-minutes-button"
              >
                <Send className="w-4 h-4 mr-2" /> Salvar e publicar ao cliente
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* If meeting is far in the future, gentle reminder */}
      {hasMeet && inFuture && !isLive && !isDone && (
        <div className="text-xs text-slate-500 text-center">
          A reunião está agendada para{" "}
          <strong>{formatDateTimeBR(meeting.scheduled_at)}</strong>. Você pode
          entrar a qualquer momento — o Meet permanece aberto até o final do
          dia.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fase 9 — Card de Recuperação Automática Google Meet + Drive
// ---------------------------------------------------------------------------
function RecoveryStatusCard({ meeting, onRecover, recovering }) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get(`/meetings/${meeting.id}/recovery-status`);
      setHistory(data);
    } catch (err) {
      toast.error("Não foi possível carregar o histórico.");
    } finally {
      setLoadingHistory(false);
    }
  };

  const toggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && !history) loadHistory();
  };
  const status = meeting.auto_recovery_status; // null|waiting|in_progress|partial|complete|failed|disabled|manual
  const hasRecording = !!meeting.drive_recording_file_id;
  const hasTranscript = !!meeting.transcript_recovered_at;
  const hasNotes = !!meeting.gemini_notes_recovered_at;
  const attempts = meeting.auto_recovery_attempts || 0;
  const lastAttempt = meeting.auto_recovery_last_attempt_at;
  const isDone = meeting.status === "done";
  const isComplete = status === "complete" || (hasRecording && hasTranscript && hasNotes);
  const isFailed = status === "failed";
  const isDisabled = status === "disabled";

  // Visual style per state
  const tone = isComplete
    ? "emerald"
    : isFailed
      ? "red"
      : isDisabled
        ? "slate"
        : "blue";

  const headerLabel = isComplete
    ? "Recuperação completa"
    : isFailed
      ? "Falha na recuperação automática"
      : isDisabled
        ? "Recuperação automática desativada"
        : !isDone
          ? "Aguardando o término da reunião"
          : !status || status === "waiting"
            ? "Aguardando processamento do Google"
            : status === "in_progress"
              ? "Recuperando…"
              : status === "partial"
                ? "Recuperação parcial — continuando…"
                : "Recuperação automática";

  const HeaderIcon = isComplete
    ? CheckCircle2
    : isFailed
      ? XCircle
      : isDisabled
        ? CircleOff
        : recovering || status === "in_progress"
          ? Loader2
          : Hourglass;

  const toneClasses = {
    emerald: {
      border: "border-emerald-200",
      bg: "bg-emerald-50/40",
      icon: "text-emerald-700",
      title: "text-emerald-900",
    },
    red: {
      border: "border-red-200",
      bg: "bg-red-50/40",
      icon: "text-red-700",
      title: "text-red-900",
    },
    slate: {
      border: "border-slate-200",
      bg: "bg-slate-50/60",
      icon: "text-slate-600",
      title: "text-slate-800",
    },
    blue: {
      border: "border-blue-200",
      bg: "bg-blue-50/30",
      icon: "text-blue-700",
      title: "text-slate-900",
    },
  }[tone];

  return (
    <Card
      className={`card-soft ${toneClasses.border} ${toneClasses.bg}`}
      data-testid="meeting-auto-recovery-card"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <HeaderIcon
            className={`w-4 h-4 ${toneClasses.icon} ${
              recovering || status === "in_progress" ? "animate-spin" : ""
            }`}
          />
          <span className={toneClasses.title}>{headerLabel}</span>
          {attempts > 0 && (
            <Badge
              className="ml-auto bg-white border border-slate-200 text-slate-600 hover:bg-white text-[10px]"
              data-testid="recovery-attempts-badge"
            >
              {attempts} tentativa{attempts === 1 ? "" : "s"}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Timeline */}
        <ul className="space-y-2" data-testid="recovery-timeline">
          <RecoveryStep
            done={hasRecording}
            icon={Film}
            label="Gravação (Drive)"
            timestamp={meeting.recording_recovered_at}
            sublabel={
              hasRecording
                ? "Vídeo MP4 recuperado e disponível no player"
                : "O vídeo aparece aqui após o Meet finalizar o processamento."
            }
            failed={isFailed && !hasRecording}
            testid="recovery-step-recording"
          />
          <RecoveryStep
            done={hasTranscript}
            icon={Mic}
            label="Transcrição"
            timestamp={meeting.transcript_recovered_at}
            sublabel={
              hasTranscript
                ? "Texto completo da fala salvo na ata."
                : "Disponível somente se a reunião teve transcrição ativada."
            }
            failed={isFailed && !hasTranscript}
            testid="recovery-step-transcript"
          />
          <RecoveryStep
            done={hasNotes}
            icon={Sparkles}
            label="Notas do Gemini"
            timestamp={meeting.gemini_notes_recovered_at}
            sublabel={
              hasNotes
                ? "Ata preenchida automaticamente como rascunho (revise e publique)."
                : 'Disponível somente se o anfitrião usou "Take notes for me".'
            }
            failed={isFailed && !hasNotes}
            testid="recovery-step-notes"
          />
        </ul>

        {/* Error message */}
        {isFailed && meeting.auto_recovery_error && (
          <div
            className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-3"
            data-testid="recovery-error-text"
          >
            <strong>Detalhes:</strong> {meeting.auto_recovery_error}
          </div>
        )}

        {/* Footer: meta + action */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-1 border-t border-slate-200/60">
          <div className="text-[11px] text-slate-500">
            {lastAttempt ? (
              <>
                Última tentativa:{" "}
                <strong>{formatDateTimeBR(lastAttempt)}</strong>
              </>
            ) : (
              <em>O sistema verifica a cada 5 minutos após o fim da reunião.</em>
            )}
          </div>
          {!isDisabled && (
            <Button
              size="sm"
              variant={isComplete ? "outline" : "default"}
              onClick={onRecover}
              disabled={recovering}
              className={
                isComplete
                  ? ""
                  : "bg-midnight text-white hover:bg-ink"
              }
              data-testid="recovery-trigger-button"
            >
              {recovering ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {isComplete ? "Atualizar agora" : "Recuperar agora"}
            </Button>
          )}
        </div>

        {/* Helper hint when nothing exists yet */}
        {!isDone && !hasRecording && !hasTranscript && !hasNotes && (
          <div
            className="flex items-start gap-2 text-[11px] text-slate-600 bg-white/60 border border-slate-200 rounded-md p-3"
            data-testid="recovery-hint"
          >
            <Wand2 className="w-3.5 h-3.5 mt-0.5 text-blue-600 flex-shrink-0" />
            <span>
              A recuperação automática começa <strong>~10 minutos após o
              término</strong> da reunião. Para que funcione, o anfitrião do
              Meet precisa ter ativado a <strong>gravação</strong> e/ou{" "}
              <strong>&ldquo;Take notes for me&rdquo;</strong> (Gemini) durante a reunião.
            </span>
          </div>
        )}

        {/* Histórico de tentativas (toggle) */}
        {(attempts > 0 || isFailed || status === "partial") && (
          <div className="border-t border-slate-200/60 pt-3">
            <button
              type="button"
              onClick={toggleHistory}
              className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2 inline-flex items-center gap-1"
              data-testid="recovery-history-toggle"
            >
              {historyOpen ? "Ocultar" : "Ver"} histórico de tentativas
              {history?.history?.length ? ` (${history.history.length})` : ""}
            </button>
            {historyOpen && (
              <div
                className="mt-2 bg-white/50 border border-slate-200 rounded-md p-2 max-h-60 overflow-y-auto"
                data-testid="recovery-history-list"
              >
                {loadingHistory && !history && (
                  <div className="flex items-center justify-center py-3">
                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  </div>
                )}
                {history?.history && history.history.length === 0 && (
                  <p className="text-[11px] text-slate-500 italic text-center py-2">
                    Sem eventos registrados ainda.
                  </p>
                )}
                {history?.history && history.history.length > 0 && (
                  <ul className="space-y-1 text-[11px]">
                    {[...history.history].reverse().map((ev, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 border-b border-slate-100 last:border-0 pb-1 last:pb-0"
                      >
                        <span className="text-slate-400 font-mono text-[10px] mt-0.5 flex-shrink-0">
                          {ev.at ? formatDateTimeBR(ev.at).slice(0, 16) : "—"}
                        </span>
                        <div className="flex-1 min-w-0">
                          <span
                            className={`font-medium ${
                              ev.event === "complete"
                                ? "text-emerald-700"
                                : ev.event?.includes("recovered")
                                  ? "text-emerald-700"
                                  : ev.event === "meet_api_unavailable"
                                    ? "text-amber-700"
                                    : ev.event === "waiting_conference_record"
                                      ? "text-slate-600"
                                      : "text-slate-800"
                            }`}
                          >
                            {ev.event}
                          </span>
                          {ev.detail && (
                            <span className="text-slate-500 ml-2 break-words">
                              {ev.detail}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecoveryStep({ done, icon: Icon, label, timestamp, sublabel, failed, testid }) {
  const status = done ? "done" : failed ? "failed" : "pending";
  const colors = {
    done: { bg: "bg-emerald-100", text: "text-emerald-700", label: "text-slate-900" },
    failed: { bg: "bg-red-100", text: "text-red-700", label: "text-slate-700" },
    pending: { bg: "bg-slate-100", text: "text-slate-500", label: "text-slate-700" },
  }[status];

  return (
    <li className="flex items-start gap-3" data-testid={testid}>
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${colors.bg}`}
      >
        {done ? (
          <CheckCircle2 className={`w-4 h-4 ${colors.text}`} />
        ) : failed ? (
          <XCircle className={`w-4 h-4 ${colors.text}`} />
        ) : (
          <Icon className={`w-4 h-4 ${colors.text}`} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-medium ${colors.label}`}>{label}</span>
          {done && timestamp && (
            <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              {formatDateTimeBR(timestamp)}
            </span>
          )}
          {failed && (
            <span className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
              não recuperado
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-0.5">{sublabel}</p>
      </div>
    </li>
  );
}
